// Super-admin Self-Construction board. A Kanban of build-tickets that a
// headless Claude operator implements + deploys. Columns: Pending → Approved →
// In Progress → Completed (+ Failed). The whole runner is gated by a master
// switch (PlatformConfig SELF_CONSTRUCT_ENABLED) toggled here.
import React, { useEffect, useState, useCallback } from "react";
import { get, post, putGeneral, remove } from "../../api";

const COLUMNS = [
  { key: "pending", name: "Ticket Pending", color: "#64748b" },
  { key: "approved", name: "Ticket Approved", color: "#2563eb" },
  { key: "building", name: "Ticket In Progress", color: "#d97706" },
  { key: "completed", name: "Ticket Completed", color: "#16a34a" },
  { key: "failed", name: "Failed", color: "#dc2626" },
];
// The Archived column is a HIDDEN tab — it is not part of the default board.
// It only appears once the super-admin reveals it ("Show archived"). Tickets
// land here via the per-card Archive button and leave (back to Pending) via the
// same button when already archived.
const ARCHIVED_COL = { key: "archived", name: "Archived", color: "#7c3aed" };

// The "Repos" section of a ticket: which brand servers/repos the operator may
// touch. Options are grouped by SERVER. Prigmar repos have local dev trees on
// this box; the other brands (BidLight, Curlight, Free-builder) are REMOTE — the
// operator reaches them over SSH using the access method + keys/passwords saved
// in the Server Access vault (/admin/server-access), keyed by `serverKey`. That
// vault is where earlier Claude sessions stored those credentials, so a ticket
// scoped to a remote brand carries an implicit reference to them. Keep this in
// sync with the worker's REPO_CATALOG (prigmar-worker selfConstructProcessor.js).
const REPO_GROUPS = [
  { server: "Prigmar", serverKey: "prigmar", remote: false, repos: ["prigmar-app", "prigmar-worker", "prigmar-portal"] },
  { server: "BidLight", serverKey: "bidlight", remote: true, repos: ["bidlight-mern"] },
  { server: "Curlight", serverKey: "curlight", remote: true, repos: ["curlight"] },
  { server: "Free-builder", serverKey: "free-builder", remote: true, repos: ["free-builder"] },
];
// Default selection for a fresh ticket — the two Prigmar repos most tickets touch.
const DEFAULT_REPOS = ["prigmar-app", "prigmar-worker"];
// Every selectable repo key, flattened. Pure + exported for tests.
export function allRepoKeys() { return REPO_GROUPS.flatMap((g) => g.repos); }
// Where a repo's access lives: the Server Access vault key + whether it's a
// remote brand server. Lets a ticket scoped to a repo point at its credentials.
// Pure + exported for tests.
export function repoAccessRef(key) {
  const g = REPO_GROUPS.find((grp) => grp.repos.includes(key));
  return g ? { server: g.server, serverKey: g.serverKey, remote: g.remote } : null;
}

// Grouped repo checkboxes for the create + edit forms. Remote brands carry a 🔑
// — a reminder the operator reaches them over SSH with the credentials saved in
// the Server Access vault (per-brand `serverKey`).
function RepoPicker({ value, onChange }) {
  const toggle = (r, checked) => onChange(checked ? [...value, r] : value.filter((x) => x !== r));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
      {REPO_GROUPS.map((g) => (
        <div key={g.serverKey} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span
            title={`Access (SSH host, auth method, keys/passwords) is stored in Server Access → ${g.serverKey}`}
            style={{ fontSize: 11, fontWeight: 600, color: "#475569", minWidth: 90 }}
          >
            {g.server}{g.remote ? " 🔑" : ""}
          </span>
          {g.repos.map((r) => (
            <label key={r} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={value.includes(r)} onChange={(e) => toggle(r, e.target.checked)} />
              {r}
            </label>
          ))}
        </div>
      ))}
      <div style={{ fontSize: 10, color: "#94a3b8" }}>🔑 remote brand server — reached over SSH using the access method + keys/passwords saved in <strong>Server Access</strong>.</div>
    </div>
  );
}

// Board column sizing. Every normal column is an equal track with a 230px floor;
// the gap between columns matches the flex `gap` on the board container.
const COL_MIN = 230;
const COL_GAP = 12;
// When the Archived tab is revealed it takes OVER the board: every other column
// is hidden so the archived panel is the sole column. It grows to claim most of
// the body but is capped at the combined width of all the working columns (gaps
// included) so on an ultra-wide screen it stays a centered panel rather than
// stretching edge-to-edge. The board container centers it (justifyContent).
const ARCHIVED_SOLO_MAX = COL_MIN * COLUMNS.length + COL_GAP * (COLUMNS.length - 1); // 1198

// The columns to render on the board. The Archived tab is hidden by default;
// when `showArchived` is on it REPLACES the working columns (it is the only
// column) so it isn't squeezed into a thin strip beside them. Pure + exported
// for tests.
export function boardColumns(showArchived) {
  return showArchived ? [ARCHIVED_COL] : COLUMNS;
}

// Flex sizing for a board column. When shown, the Archived column is the only
// column, so it grows to claim most of the body, capped at ARCHIVED_SOLO_MAX
// (the combined width of all working columns) and centered by the board
// container. Every other column stays an equal 230px-min track. The board
// scrolls horizontally (overflowX) when these widths exceed the viewport. Pure
// + exported so the sizing can't silently drift.
export function columnLayout(col) {
  if (col && col.key === "archived") {
    return { flex: `1 1 ${ARCHIVED_SOLO_MAX}px`, minWidth: COL_MIN, maxWidth: ARCHIVED_SOLO_MAX };
  }
  return { flex: `1 0 ${COL_MIN}px`, minWidth: COL_MIN };
}

// The per-card archive control. An archived ticket's button un-archives it back
// to 'pending'; any other ticket's button archives it. Pure + exported for
// tests so the move-target + label can't silently drift.
export function archiveControl(t) {
  const archived = (t && t.status) === "archived";
  return archived
    ? { archived: true, label: "♻", target: "pending", title: "Un-archive — send back to pending" }
    : { archived: false, label: "📦", target: "archived", title: "Archive this ticket" };
}

// Format a date-ish value for the detail modal; tolerant of missing/garbage.
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// Build the labelled rows shown in the "view ticket" modal. Unlike the card
// (which truncates), this returns the FULL content so the user can review the
// whole ticket after creation. Pure + exported for tests.
export function ticketDetailRows(t) {
  if (!t) return [];
  const rows = [
    { label: "Status", value: t.status || "pending" },
    { label: "Repos", value: (t.repos || []).join(", ") },
  ];
  if (t.description) rows.push({ label: "Description", value: String(t.description), pre: true });
  if (t.acceptanceCriteria) rows.push({ label: "Acceptance criteria", value: String(t.acceptanceCriteria), pre: true });
  // What the operator is waiting on (a paused ticket). `warn` renders it as a
  // distinct amber callout — it's the headline of a ticket that needs input.
  if (t.infoRequest) rows.push({ label: "Needs input", value: String(t.infoRequest), pre: true, warn: true });
  // The answer(s) the user has already supplied (fed into the operator's prompt).
  if (t.extraInfo) rows.push({ label: "Info you provided", value: String(t.extraInfo), pre: true });
  // How the operator built it — process, technical explanation, red flags /
  // warnings. Recorded on completion; surfaced here so the user can review it.
  // `highlight` renders it as a distinct callout (it's the headline of a
  // completed self-construct ticket) rather than a buried text row.
  if (t.solutionNotes) rows.push({ label: "How it was built", value: String(t.solutionNotes), pre: true, highlight: true });
  if (t.commit) rows.push({ label: "Commit", value: String(t.commit) });
  rows.push({ label: "Deployed", value: t.deployed ? "yes" : "no" });
  if (t.rolledBack) rows.push({ label: "Rolled back", value: "yes — stopped by user, this ticket's commits were reverted" });
  if (t.lastError) rows.push({ label: "Last error", value: String(t.lastError), pre: true });
  if (t.createdAt) rows.push({ label: "Created", value: fmtDate(t.createdAt) });
  if (t.updatedAt) rows.push({ label: "Updated", value: fmtDate(t.updatedAt) });
  return rows;
}

export default function SelfConstruct() {
  const [tickets, setTickets] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openLog, setOpenLog] = useState(null);
  const [openTicket, setOpenTicket] = useState(null);
  const [draft, setDraft] = useState({ title: "", description: "", acceptanceCriteria: "", repos: [...DEFAULT_REPOS] });
  const [busy, setBusy] = useState(false);
  // Per-ticket text-area content for answering a paused ticket's info request.
  const [infoDrafts, setInfoDrafts] = useState({});
  // Edit-ticket modal: a working draft of the ticket being edited (null = closed).
  const [editDraft, setEditDraft] = useState(null);
  // Reveal the hidden Archived tab (off by default — archived tickets are out of
  // the way until the super-admin asks to see them).
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await get("/api/admin/self-construct");
      const d = (r && r.data) || r;
      setTickets((d && d.tickets) || []);
      setEnabled(!!(d && d.enabled));
    } catch (e) { /* surface nothing fatal */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const move = async (id, status) => { await putGeneral(`/api/admin/self-construct/${id}`, { status }); load(); };
  const del = async (id) => { if (window.confirm("Delete this ticket?")) { await remove(`/api/admin/self-construct/${id}`, {}); load(); } };
  const run = async (id) => { setBusy(true); try { await post(`/api/admin/self-construct/${id}/run`, {}); } catch (e) {} setBusy(false); load(); };
  // Answer a paused ticket's info request: post the user's input, clear the
  // draft, and re-load (the ticket flips back to approved and re-runs with the
  // info injected into the operator's prompt). Closes the modal if open.
  const resume = async (id) => {
    const info = (infoDrafts[id] || "").trim();
    if (!info) return;
    setBusy(true);
    try {
      await post(`/api/admin/self-construct/${id}/resume`, { info });
      setInfoDrafts((d) => ({ ...d, [id]: "" }));
      setOpenTicket(null);
    } catch (e) {}
    setBusy(false); load();
  };
  const toggleEnabled = async () => {
    const next = !enabled; setEnabled(next);
    try { await putGeneral("/api/admin/self-construct/settings/enabled", { enabled: next }); } catch (e) { setEnabled(!next); }
  };
  const create = async () => {
    if (!draft.title.trim()) return;
    setBusy(true);
    try { await post("/api/admin/self-construct", draft); setDraft({ title: "", description: "", acceptanceCriteria: "", repos: [...DEFAULT_REPOS] }); } catch (e) {}
    setBusy(false); load();
  };
  // Stop a running ticket AND undo its changes: kills the operator and reverts
  // only this ticket's commits across its repos (dev + prod). Destructive →
  // confirm first.
  const stop = async (id) => {
    if (!window.confirm("Stop this ticket and roll back ALL changes it made (dev + prod), restarting affected services?")) return;
    setBusy(true);
    try { await post(`/api/admin/self-construct/${id}/stop`, {}); } catch (e) {}
    setBusy(false); load();
  };
  // Open the edit modal pre-filled from a ticket.
  const openEdit = (t) => setEditDraft({
    _id: t._id,
    title: t.title || "",
    description: t.description || "",
    acceptanceCriteria: t.acceptanceCriteria || "",
    repos: t.repos && t.repos.length ? [...t.repos] : [...DEFAULT_REPOS],
  });
  // Save edits (PUT). When `thenRun` is true, re-run immediately so the next
  // build picks up the edited spec/repos.
  const saveEdit = async (thenRun) => {
    if (!editDraft || !editDraft.title.trim()) return;
    setBusy(true);
    try {
      await putGeneral(`/api/admin/self-construct/${editDraft._id}`, {
        title: editDraft.title,
        description: editDraft.description,
        acceptanceCriteria: editDraft.acceptanceCriteria,
        repos: editDraft.repos,
      });
      const id = editDraft._id;
      setEditDraft(null);
      if (thenRun) { try { await post(`/api/admin/self-construct/${id}/run`, {}); } catch (e) {} }
    } catch (e) {}
    setBusy(false); load();
  };

  // 'paused' is a sub-state of "In Progress" (the operator parked the ticket
  // awaiting user input) — render those cards in the building column.
  const byCol = (k) => tickets.filter((t) => {
    const s = t.status || "pending";
    if (k === "building") return s === "building" || s === "paused";
    return s === k;
  });
  const archivedCount = tickets.filter((t) => (t.status || "pending") === "archived").length;
  const actions = (t) => {
    const edit = ["✎ Edit", () => openEdit(t), "#0ea5e9"];
    const stopBtn = ["■ Stop & roll back", () => stop(t._id), "#dc2626"];
    switch (t.status) {
      case "pending":   return [["Approve", () => move(t._id, "approved"), "#2563eb"], edit];
      case "approved":  return [["▶ Run", () => run(t._id), "#16a34a"], ["↩ Pending", () => move(t._id, "pending"), "#64748b"], edit];
      case "building":  return [stopBtn];
      case "paused":    return [stopBtn, edit];
      case "completed": return [["↻ Re-open", () => move(t._id, "approved"), "#2563eb"], edit];
      case "failed":    return [["↻ Retry", () => move(t._id, "approved"), "#d97706"], edit];
      default: return [edit];
    }
  };

  return (
    <div style={{ padding: 20, fontFamily: "-apple-system, Segoe UI, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Self-Construction</h3>
          {/* Reveal/hide the hidden Archived tab. Shows how many tickets are shelved. */}
          <button
            onClick={() => setShowArchived((s) => !s)}
            style={{ padding: "4px 10px", fontSize: 12, background: showArchived ? ARCHIVED_COL.color : "#ede9fe", color: showArchived ? "#fff" : ARCHIVED_COL.color, border: `1px solid ${ARCHIVED_COL.color}`, borderRadius: 6, cursor: "pointer" }}
            title={showArchived ? "Hide the archived tab" : "Show the archived tab"}
          >
            {showArchived ? "Hide archived" : `📦 Show archived (${archivedCount})`}
          </button>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
          <span style={{ color: enabled ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
            Runner {enabled ? "ENABLED" : "DISABLED"}
          </span>
          <input type="checkbox" checked={enabled} onChange={toggleEnabled} />
        </label>
      </div>
      {!enabled && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", padding: "8px 12px", borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          The autonomous runner is OFF. Approved tickets sit until you enable it — then they implement + deploy themselves, one at a time.
        </div>
      )}

      {/* New ticket */}
      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, marginBottom: 20 }}>
        <input style={{ width: "100%", padding: 8, marginBottom: 6, boxSizing: "border-box" }} placeholder="Ticket title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        <textarea style={{ width: "100%", padding: 8, marginBottom: 6, boxSizing: "border-box", minHeight: 60 }} placeholder="What to build (the spec)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        <textarea style={{ width: "100%", padding: 8, marginBottom: 6, boxSizing: "border-box", minHeight: 40 }} placeholder="Acceptance criteria (optional)" value={draft.acceptanceCriteria} onChange={(e) => setDraft({ ...draft, acceptanceCriteria: e.target.value })} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>Repos:</span>
          <RepoPicker value={draft.repos} onChange={(repos) => setDraft({ ...draft, repos })} />
          <button disabled={busy || !draft.title.trim()} onClick={create} style={{ alignSelf: "flex-end", padding: "6px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>Add ticket</button>
        </div>
      </div>

      {/* Board. When the Archived tab is revealed it is the only column, so
          center it (it claims most of the body); otherwise the working columns
          start from the left. */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", overflowX: "auto", justifyContent: showArchived ? "center" : "flex-start" }}>
        {boardColumns(showArchived).map((col) => (
          <div key={col.key} style={{ ...columnLayout(col), background: "#f1f5f9", borderRadius: 8, padding: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: col.color, borderBottom: `2px solid ${col.color}`, paddingBottom: 6, marginBottom: 8 }}>
              {col.name} <span style={{ color: "#94a3b8" }}>({byCol(col.key).length})</span>
            </div>
            {byCol(col.key).map((t) => (
              <div key={t._id} onClick={() => setOpenTicket(t)} title="Click to view ticket" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: 10, marginBottom: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.04)", cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                  <strong style={{ fontSize: 13 }}>{t.title}</strong>
                  {/* Delete (top) with the Archive / un-archive control stacked
                      directly below it, on every ticket. */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <span onClick={(e) => { e.stopPropagation(); del(t._id); }} style={{ cursor: "pointer", color: "#cbd5e1", fontSize: 14 }} title="Delete">🗑</span>
                    {(() => {
                      const a = archiveControl(t);
                      return (
                        <span onClick={(e) => { e.stopPropagation(); move(t._id, a.target); }} style={{ cursor: "pointer", color: a.archived ? ARCHIVED_COL.color : "#cbd5e1", fontSize: 14 }} title={a.title}>{a.label}</span>
                      );
                    })()}
                  </div>
                </div>
                {t.description ? <div style={{ fontSize: 12, color: "#475569", marginTop: 4, whiteSpace: "pre-wrap" }}>{String(t.description).slice(0, 220)}</div> : null}
                <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>{(t.repos || []).join(", ")}{t.commit ? ` · ${String(t.commit).slice(0, 8)}` : ""}{t.deployed ? " · deployed" : ""}{t.solutionNotes ? " · 📝 notes" : ""}</div>
                {t.status === "building" && <div style={{ fontSize: 11, color: "#d97706", marginTop: 4 }}>● building…</div>}
                {t.status === "paused" && (
                  // The operator parked this ticket needing input — show what it
                  // asked for + a text area and a Resume button right on the card.
                  <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 6, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#b45309", marginBottom: 4 }}>⏸ Needs input</div>
                    {t.infoRequest ? <div style={{ fontSize: 12, color: "#92400e", whiteSpace: "pre-wrap", marginBottom: 6 }}>{t.infoRequest}</div> : null}
                    <textarea
                      value={infoDrafts[t._id] || ""}
                      // Read the value eagerly (not inside the functional
                      // updater) — React 16 pools the synthetic event, so a
                      // deferred e.target read sees null once the handler returns.
                      onChange={(e) => { const v = e.target.value; setInfoDrafts((d) => ({ ...d, [t._id]: v })); }}
                      placeholder="Enter the requested info (credentials, key, choice…)"
                      style={{ width: "100%", boxSizing: "border-box", minHeight: 48, fontSize: 12, padding: 6, border: "1px solid #fcd34d", borderRadius: 5 }}
                    />
                    <button disabled={busy || !(infoDrafts[t._id] || "").trim()} onClick={() => resume(t._id)} style={{ marginTop: 6, padding: "4px 12px", fontSize: 12, background: "#d97706", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" }}>▶ Resume with info</button>
                  </div>
                )}
                {t.lastError ? <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{String(t.lastError).slice(0, 200)}</div> : null}
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  {actions(t).map(([label, fn, c]) => (
                    <button key={label} disabled={busy} onClick={(e) => { e.stopPropagation(); fn(); }} style={{ padding: "3px 10px", fontSize: 12, background: c, color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" }}>{label}</button>
                  ))}
                  {t.buildLog ? <button onClick={(e) => { e.stopPropagation(); setOpenLog(openLog === t._id ? null : t._id); }} style={{ padding: "3px 10px", fontSize: 12, background: "#e2e8f0", border: "none", borderRadius: 5, cursor: "pointer" }}>{openLog === t._id ? "Hide log" : "Log"}</button> : null}
                </div>
                {openLog === t._id && (
                  <pre onClick={(e) => e.stopPropagation()} style={{ marginTop: 8, background: "#0f172a", color: "#cbd5e1", padding: 8, borderRadius: 6, fontSize: 10, maxHeight: 240, overflow: "auto", whiteSpace: "pre-wrap" }}>{t.buildLog}</pre>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      {loading && <div style={{ marginTop: 12, color: "#94a3b8" }}>Loading…</div>}

      {/* View-ticket modal — full, untruncated content for review after creation. */}
      {openTicket && (
        <div
          onClick={() => setOpenTicket(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 10, padding: 24, width: "min(760px, 100%)", maxHeight: "85vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{openTicket.title}</h3>
              <button onClick={() => setOpenTicket(null)} style={{ background: "transparent", border: "none", fontSize: 22, lineHeight: 1, cursor: "pointer", color: "#64748b" }} title="Close" aria-label="Close">×</button>
            </div>
            {ticketDetailRows(openTicket).map((row) => (
              row.warn ? (
                // Amber callout for what the operator is waiting on (paused ticket).
                <div key={row.label} style={{ marginBottom: 12, background: "#fffbeb", border: "1px solid #fde68a", borderLeft: "4px solid #d97706", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#b45309", marginBottom: 4, fontWeight: 600 }}>⏸ {row.label}</div>
                  <div style={{ fontSize: 13, color: "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{row.value}</div>
                </div>
              ) : row.highlight ? (
                // Distinct callout for the operator's solution write-up — the
                // headline of a completed self-construct ticket, so it reads as
                // a panel ("how it was built" + red flags) rather than a row.
                <div key={row.label} style={{ marginBottom: 12, background: "#f0fdf4", border: "1px solid #bbf7d0", borderLeft: "4px solid #16a34a", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#16a34a", marginBottom: 4, fontWeight: 600 }}>📝 {row.label}</div>
                  <div style={{ fontSize: 13, color: "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{row.value}</div>
                </div>
              ) : (
                <div key={row.label} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#94a3b8", marginBottom: 2 }}>{row.label}</div>
                  {row.pre
                    ? <div style={{ fontSize: 13, color: "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{row.value}</div>
                    : <div style={{ fontSize: 13, color: "#1e293b" }}>{row.value}</div>}
                </div>
              )
            ))}
            {openTicket.status === "paused" && (
              // Answer the operator's request right from the modal — same flow
              // as the card, just roomier.
              <div style={{ marginBottom: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#b45309", marginBottom: 6 }}>Provide the requested info to resume</div>
                <textarea
                  value={infoDrafts[openTicket._id] || ""}
                  // Eager read — see the card panel above (React 16 event pooling).
                  onChange={(e) => { const v = e.target.value; setInfoDrafts((d) => ({ ...d, [openTicket._id]: v })); }}
                  placeholder="Enter the requested info (credentials, key, choice…)"
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 80, fontSize: 13, padding: 8, border: "1px solid #fcd34d", borderRadius: 6 }}
                />
                <button disabled={busy || !(infoDrafts[openTicket._id] || "").trim()} onClick={() => resume(openTicket._id)} style={{ marginTop: 8, padding: "6px 16px", fontSize: 13, background: "#d97706", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>▶ Resume with info</button>
              </div>
            )}
            {openTicket.buildLog ? (
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#94a3b8", marginBottom: 2 }}>Build log</div>
                <pre style={{ background: "#0f172a", color: "#cbd5e1", padding: 10, borderRadius: 6, fontSize: 11, maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap" }}>{openTicket.buildLog}</pre>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Edit-ticket modal — change the spec/repos so a re-run uses the edits. */}
      {editDraft && (
        <div
          onClick={() => setEditDraft(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 10, padding: 24, width: "min(680px, 100%)", maxHeight: "85vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Edit ticket</h3>
              <button onClick={() => setEditDraft(null)} style={{ background: "transparent", border: "none", fontSize: 22, lineHeight: 1, cursor: "pointer", color: "#64748b" }} title="Close" aria-label="Close">×</button>
            </div>
            <input style={{ width: "100%", padding: 8, marginBottom: 6, boxSizing: "border-box" }} placeholder="Ticket title" value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} />
            <textarea style={{ width: "100%", padding: 8, marginBottom: 6, boxSizing: "border-box", minHeight: 140 }} placeholder="What to build (the spec)" value={editDraft.description} onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} />
            <textarea style={{ width: "100%", padding: 8, marginBottom: 6, boxSizing: "border-box", minHeight: 60 }} placeholder="Acceptance criteria (optional)" value={editDraft.acceptanceCriteria} onChange={(e) => setEditDraft({ ...editDraft, acceptanceCriteria: e.target.value })} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 12, color: "#64748b" }}>Repos:</span>
              <RepoPicker value={editDraft.repos} onChange={(repos) => setEditDraft({ ...editDraft, repos })} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button disabled={busy} onClick={() => setEditDraft(null)} style={{ padding: "6px 16px", background: "#e2e8f0", border: "none", borderRadius: 6, cursor: "pointer" }}>Cancel</button>
              <button disabled={busy || !editDraft.title.trim()} onClick={() => saveEdit(false)} style={{ padding: "6px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>Save</button>
              <button disabled={busy || !editDraft.title.trim()} onClick={() => saveEdit(true)} style={{ padding: "6px 16px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }} title="Save and re-run now with these changes">Save &amp; re-run</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

SelfConstruct._test = { ticketDetailRows, fmtDate, boardColumns, archiveControl, columnLayout, COLUMNS, ARCHIVED_COL, REPO_GROUPS, DEFAULT_REPOS, allRepoKeys, repoAccessRef };
